import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarDays, Check, Info, Minus, Phone, Plus } from "lucide-react";
import { postQuote } from "../lib/api";
import AddonPicker from "./AddonPicker";
import BookingOutage from "./BookingOutage";
import { money, time12 } from "../lib/format";
import { LOCATIONS } from "../lib/content";
import { useTrip } from "../hooks/useTrip";

const WB = LOCATIONS["west-branch"];

const Stepper = ({ value, min = 1, max = 9, onChange, label, testId }) => (
  <div className="inline-flex items-center gap-1 rounded-full border border-sand-border bg-white">
    <button
      type="button"
      data-testid={`${testId}-decrement`}
      aria-label={`Decrease ${label}`}
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-navy disabled:opacity-30"
    >
      <Minus className="h-4 w-4" aria-hidden="true" />
    </button>
    <span data-testid={`${testId}-value`} aria-live="polite" className="w-7 text-center text-sm font-bold text-navy">
      {value}
    </span>
    <button
      type="button"
      data-testid={`${testId}-increment`}
      aria-label={`Increase ${label}`}
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-navy disabled:opacity-30"
    >
      <Plus className="h-4 w-4" aria-hidden="true" />
    </button>
  </div>
);

export const BookingPanel = ({ boat, date, minDate, onDateChange, showDegraded }) => {
  const navigate = useNavigate();
  const { addLine } = useTrip();

  const tiers = boat?.rates?.tiers || [];
  const [tierKey, setTierKey] = useState(null);
  const [departure, setDeparture] = useState(null);
  const [qty, setQty] = useState(1);
  const [acks, setAcks] = useState({});
  const [addonQty, setAddonQty] = useState({});
  const [quote, setQuote] = useState(null);
  const [added, setAdded] = useState(false);
  const quoteSeq = useRef(0);

  const keyOf = (t) => `${t.priceId}-${t.durationHours}`;
  const tier = tiers.find((t) => keyOf(t) === tierKey) || null;

  useEffect(() => {
    setTierKey(null);
    setDeparture(null);
    setQuote(null);
    setAdded(false);
  }, [date, boat?.assetId]);

  useEffect(() => setDeparture(null), [tierKey]);

  const requiredAcks = boat?.listing?.acknowledgementsRequired || [];
  const outstandingAcks = requiredAcks.filter((a) => !acks[a.policyMapId]);

  const maxQty = useMemo(() => {
    const counts = [];
    if (typeof boat?.availableCount === "number") counts.push(boat.availableCount);
    if (tier?.maxQuantity && tier.priceUnit !== "per_hour") counts.push(tier.maxQuantity);
    return counts.length ? Math.max(1, Math.min(...counts)) : 9;
  }, [boat, tier]);

  const addons = boat?.addons || [];
  const selectedAddons = useMemo(
    () => addons
      .filter((a) => (addonQty[a.assetId] || 0) > 0)
      .map((a) => ({ ...a, quantity: addonQty[a.assetId] })),
    [addons, addonQty],
  );

  // Vendor quote is the ONLY source of truth for money. Debounced, sequenced so a
  // stale response can never overwrite a newer total, and the vendor re-prices
  // anyway -- we never send an amount.
  useEffect(() => {
    if (!tier || !departure) {
      setQuote(null);
      return;
    }
    const seq = ++quoteSeq.current;
    const lines = [
      {
        assetId: boat.assetId,
        serviceId: boat.serviceIds?.[0] ?? null,
        priceId: tier.priceId,
        quantity: qty,
        label: `${boat.displayName} for ${tier.label}`,
        startDateTime: departure.startDateTime,
        endDateTime: departure.endDateTime,
      },
      ...selectedAddons.map((a) => ({
        assetId: a.assetId,
        serviceId: a.serviceId,
        priceId: a.priceId,
        quantity: a.quantity,
        label: a.label,
        startDateTime: departure.startDateTime,
        endDateTime: departure.endDateTime,
      })),
    ];
    const timer = setTimeout(() => {
      postQuote(boat.locationId, lines)
        .then((q) => {
          if (seq === quoteSeq.current) setQuote(q);
        })
        .catch(() => {
          if (seq === quoteSeq.current) setQuote(null);
        });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, departure, qty, selectedAddons, boat?.assetId]);

  const blockers = [];
  if (!tier) blockers.push("choose a duration");
  if (tier && !departure) blockers.push("choose a start time");
  outstandingAcks.forEach((a) => blockers.push(`accept the ${a.heading}`));
  const canAdd = blockers.length === 0;

  const addToTrip = () => {
    addLine({
      locationId: boat.locationId,
      assetId: boat.assetId,
      displayName: boat.displayName,
      assetTypeName: boat.assetTypeName,
      heroImageUrl: boat.listing?.heroImageUrl || null,
      serviceId: boat.serviceIds?.[0] ?? null,
      serviceName: boat.serviceName,
      priceId: tier.priceId,
      tierLabel: tier.label,
      priceUnit: tier.priceUnit,
      durationHours: tier.durationHours,
      amount: tier.amount,
      quantity: qty,
      date,
      startTime: departure.startTime,
      endTime: departure.endTime,
      startDateTime: departure.startDateTime,
      endDateTime: departure.endDateTime,
      // Add-ons ride the same order as their own lines under service 13.
      addons: selectedAddons.map((a) => ({
        assetId: a.assetId,
        serviceId: a.serviceId,
        priceId: a.priceId,
        label: a.label,
        amount: a.amount,
        quantity: a.quantity,
      })),
      acknowledgedPolicyIds: requiredAcks.map((a) => a.policyMapId),
      acknowledgedPolicyHeadings: requiredAcks.map((a) => a.heading),
      requiredAckCount: requiredAcks.length,
    });
    setAdded(true);
  };

  const addonsDegraded = (boat?.degraded || []).includes("availability");
  // No tiers because availability failed = outage. No tiers with healthy
  // availability = genuinely nothing bookable that day.
  const outage = (boat?.degraded || []).includes("availability");

  if (outage) {
    return <BookingOutage testId="booking-panel-outage" compact />;
  }

  return (
    <div data-testid="booking-panel" className="rounded-3xl border border-sand-border bg-white p-6 shadow-xl shadow-navy/5">
      <div>
        <label htmlFor="bp-date" className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Rental date
        </label>
        <div className="relative mt-2">
          <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            id="bp-date"
            type="date"
            data-testid="booking-panel-date-input"
            value={date}
            min={minDate}
            onChange={(e) => e.target.value && onDateChange(e.target.value)}
            className="h-12 w-full rounded-xl border border-sand-border bg-sand pl-9 pr-3 text-sm font-semibold text-navy"
          />
        </div>
      </div>

      {tiers.length === 0 ? (
        <div data-testid="booking-panel-no-rates" className="mt-6 rounded-xl bg-sand-card p-4 text-sm text-slate-700">
          <p>This boat isn't bookable on {date} — it may be out of season or already taken.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/boat-rentals/west-branch"
              data-testid="no-rates-browse-link"
              className="rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-navy-surface"
            >
              See what's available
            </Link>
            <a
              href={WB.phoneHref}
              data-testid="no-rates-call-link"
              className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-xs font-bold text-navy hover:bg-white"
            >
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              Call {WB.phone}
            </a>
          </div>
        </div>
      ) : (
        <>
          <fieldset className="mt-7">
            <legend className="eyebrow text-lake">Select duration</legend>
            <div data-testid="duration-tiles" className="mt-3 grid gap-3 sm:grid-cols-2">
              {tiers.map((t) => {
                const active = keyOf(t) === tierKey;
                return (
                  <button
                    key={keyOf(t)}
                    type="button"
                    data-testid={`duration-tile-${t.priceId}-${t.durationHours}`}
                    aria-pressed={active}
                    onClick={() => setTierKey(keyOf(t))}
                    className={`rounded-2xl border-2 p-4 text-left transition-all ${
                      active ? "border-ember bg-ember/[0.06]" : "border-sand-border hover:border-lake/50"
                    }`}
                  >
                    <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t.isDayRate ? "Day rate" : `${t.durationHours} hour${t.durationHours === 1 ? "" : "s"}`}
                    </span>
                    <span className="mt-1 block font-display text-lg font-bold text-navy">{money(t.amount)}</span>
                    {t.savingsPercent ? (
                      <span
                        data-testid={`duration-tile-savings-${t.priceId}`}
                        title="Calculated by comparing the day rate with the hourly rate for the same hours"
                        className="mt-2 inline-block rounded-full bg-lake/10 px-2 py-0.5 text-[11px] font-bold text-lake"
                      >
                        {t.savingsPercent}% less than hourly
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {tier && (
            <fieldset className="mt-7">
              <legend className="eyebrow text-lake">Select start time</legend>
              <div data-testid="departure-tiles" className="mt-3 flex flex-wrap gap-2">
                {tier.departures.map((d) => {
                  const active = departure?.startDateTime === d.startDateTime;
                  return (
                    <button
                      key={d.startDateTime}
                      type="button"
                      data-testid={`departure-tile-${d.startTime}`}
                      aria-pressed={active}
                      onClick={() => setDeparture(d)}
                      className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-all ${
                        active ? "border-ember bg-ember text-white" : "border-sand-border text-navy hover:border-lake/50"
                      }`}
                    >
                      {time12(d.startTime)}
                    </button>
                  );
                })}
              </div>
              {departure && (
                <dl data-testid="departure-summary" className="mt-4 grid grid-cols-2 gap-3 rounded-2xl bg-sand-card p-4 text-sm">
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Check-in</dt>
                    <dd className="font-display text-base font-bold text-navy">{time12(departure.startTime)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Check-out</dt>
                    <dd className="font-display text-base font-bold text-navy">{time12(departure.endTime)}</dd>
                  </div>
                </dl>
              )}
            </fieldset>
          )}

          <div className="mt-7 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-navy"># of vessels</p>
              <p className="text-xs text-slate-500">
                {typeof boat.availableCount === "number"
                  ? `${boat.availableCount} available`
                  : "This is a single, individually-tracked boat"}
              </p>
            </div>
            <Stepper value={qty} min={1} max={maxQty} onChange={setQty} label="number of vessels" testId="qty-stepper" />
          </div>

          {addons.length > 0 && (
            <AddonPicker
              addons={addons}
              quantities={addonQty}
              onChange={(assetId, v) => setAddonQty((s) => ({ ...s, [assetId]: v }))}
            />
          )}

          {addons.length === 0 && addonsDegraded && showDegraded && (
            <div
              data-testid="addons-degraded-banner"
              role="status"
              className="mt-7 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
              <p className="text-sm text-amber-900">
                <strong className="font-bold">Internal notice:</strong> the add-ons list is unavailable, so the add-ons
                section is hidden.
              </p>
            </div>
          )}

          {quote && (
            <div data-testid="price-summary" className="mt-7 rounded-2xl border border-sand-border bg-sand p-5">
              <p className="eyebrow text-lake">Booking amount</p>
              <dl className="mt-4 space-y-2.5 text-sm">
                {quote.lines.map((l) => (
                  <div key={l.lineNo} className="flex justify-between gap-4">
                    <dt className="text-slate-700">
                      {l.priceName}
                      {l.quantity > 1 ? ` × ${l.quantity}` : ""}
                    </dt>
                    <dd data-testid="price-summary-base" className="font-semibold text-navy">
                      {money(l.baseAmount)}
                    </dd>
                  </div>
                ))}

                {quote.orderFees > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-700">Fees</dt>
                    <dd data-testid="price-summary-fees" className="font-semibold text-navy">{money(quote.orderFees)}</dd>
                  </div>
                )}

                {quote.orderTax > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-700">
                      Sales tax{quote.taxRateLabel ? ` (${quote.taxRateLabel})` : ""}
                    </dt>
                    <dd data-testid="price-summary-tax" className="font-semibold text-navy">{money(quote.orderTax)}</dd>
                  </div>
                )}

                <div className="mt-4 flex justify-between gap-4 border-t border-sand-border pt-4">
                  <dt className="font-display text-base font-bold text-navy">Total</dt>
                  <dd data-testid="price-summary-total" className="font-display text-xl font-bold text-navy">
                    {money(quote.orderTotal)}
                  </dd>
                </div>

                {quote.hasDeposit && (
                  <>
                    <div className="flex justify-between gap-4">
                      <dt className="font-bold text-navy">Pay today</dt>
                      <dd data-testid="price-summary-pay-today" className="font-bold text-navy">
                        {money(quote.payTodayAmount)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-slate-700">Balance due at the marina</dt>
                      <dd data-testid="price-summary-balance" className="font-semibold text-navy">
                        {money(quote.balanceDueAmount)}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
              <p data-testid="price-summary-note" className="mt-3 flex items-start gap-2 text-xs text-slate-500">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {quote.hasDeposit
                  ? `You'll pay ${money(quote.payTodayAmount)} now to hold the boat, and the remaining ${money(quote.balanceDueAmount)} when you check in at the dock.`
                  : "This is the full price, charged when you book."}
              </p>
            </div>
          )}

          {requiredAcks.length > 0 && (
            <fieldset data-testid="ack-section" className="mt-7">
              <legend className="eyebrow text-lake">Required acknowledgements</legend>
              <div className="mt-3 space-y-3">
                {requiredAcks.map((a) => (
                  <label
                    key={a.policyMapId}
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-sand-border p-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      data-testid={`ack-checkbox-${a.policyMapId}`}
                      checked={Boolean(acks[a.policyMapId])}
                      onChange={(e) => setAcks((s) => ({ ...s, [a.policyMapId]: e.target.checked }))}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-sand-border accent-[#D96B27]"
                    />
                    <span className="text-slate-700">
                      I have read and accept the <strong className="font-bold text-navy">{a.heading}</strong>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <button
            type="button"
            data-testid="add-to-trip-button"
            disabled={!canAdd}
            onClick={addToTrip}
            className="mt-7 w-full rounded-full bg-ember px-6 py-4 font-bold text-white transition-colors hover:bg-ember-hover disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
          >
            Add to Trip
          </button>

          {!canAdd && (
            <p data-testid="add-to-trip-blockers" className="mt-3 text-sm text-slate-600">
              To continue, please {blockers.join(", ")}.
            </p>
          )}

          {added && (
            <div
              data-testid="added-to-trip-confirmation"
              role="status"
              className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-lake/30 bg-lake/[0.07] p-4"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-navy">
                <Check className="h-4 w-4 text-lake" aria-hidden="true" />
                Added to your trip
              </span>
              <button
                type="button"
                data-testid="view-trip-button"
                onClick={() => navigate("/cart")}
                className="rounded-full bg-navy px-4 py-2 text-sm font-bold text-white hover:bg-navy-surface"
              >
                View trip
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingPanel;
